<script setup lang="ts">
import { cva } from 'class-variance-authority'
import { computed, type PropType } from 'vue'
import { DateTime } from 'luxon'
import { useI18n } from 'vue-i18n'
import {
    PriceComponentType,
    type ChargePriceSimulation
} from '../../../../interfaces/simulation'

const { t, n } = useI18n()

const props = defineProps({
    priceDetails: {
        type: Object as PropType<ChargePriceSimulation>,
        default: undefined
    },
    isBoost: {
        type: Boolean,
        default: false
    },
    isDisabled: {
        type: Boolean,
        default: false
    }
})

const formattedEnergy = computed(() => {
    if (
        props.isDisabled ||
        !props.priceDetails ||
        props.priceDetails.energy === null
    ) {
        return '-- kWh'
    }

    return `${n(props.priceDetails.energy / 1000)} kWh`
})

const chargeDuration = computed(() => {
    if (props.isDisabled || !props.priceDetails) {
        return '-- min'
    }

    const startDate = DateTime.fromISO(props.priceDetails.startDate)
    const endDate = DateTime.fromISO(props.priceDetails.endDate)

    const duration = endDate.diff(startDate, ['hours', 'minutes'])
    const totalMinutes = Math.round(duration.as('minutes'))

    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    return `${t('simulation.charge.results.overview.duration', { hours, minutes })}`
})

const isChargeFree = computed(() => {
    if (props.isDisabled || !props.priceDetails) {
        return false
    }
    return parseFloat(props.priceDetails.totalTaxedPrice?.amount ?? '0') === 0
})

const boostFormattedAmount = computed(() => {
    const totalTaxedPrice = parseFloat(
        props.priceDetails?.totalTaxedPrice?.amount ?? '0'
    )

    const marginAmount = parseFloat(
        props.priceDetails?.taxedPriceComponents?.find(
            (c) => c.type === PriceComponentType.MARGIN
        )?.price?.amount ?? '0'
    )

    return n(totalTaxedPrice - marginAmount, 'currency')
})

const formattedAmount = computed(() => {
    if (props.isDisabled || !props.priceDetails) {
        return '-- €'
    }

    if (isChargeFree.value) {
        return t('simulation.charge.results.overview.free_amount')
    }

    if (props.isBoost) {
        return boostFormattedAmount.value
    }

    return n(
        parseFloat(props.priceDetails.totalTaxedPrice.amount ?? '0') / 100,
        'currency'
    )
})

const amountValueStyles = computed(() =>
    cva('text-[15px] 425:text-[17px] font-bold leading-[18px] 425:leading-5', {
        variants: {
            default: {
                true: 'text-gray-l-800'
            },
            isSpecial: {
                true: 'text-badge-positive'
            },
            isDisabled: {
                true: 'text-gray-disabled'
            }
        }
    })({
        default: !isChargeFree.value && !props.isBoost && !props.isDisabled,
        isSpecial: (isChargeFree.value || props.isBoost) && !props.isDisabled,
        isDisabled: props.isDisabled
    })
)
</script>

<template>
    <div
        class="flex w-full h-[67px] 425:h-fit px-2 py-4 425:p-4 bg-badge-neutral text-center rounded-lg justify-between items-center"
    >
        <div class="px-4 flex flex-col justify-center items-center gap-2">
            <div
                class="text-[11px] 425:text-[13px] font-normal leading-[13px] 425:leading-4"
                :class="isDisabled ? 'text-gray-disabled' : 'text-gray-l-500'"
            >
                {{ $t('simulation.charge.results.overview.labels.energy') }}
            </div>
            <div
                class="text-[15px] 425:text-[17px] font-bold leading-[18px] 425:leading-5"
                :class="isDisabled ? 'text-gray-disabled' : 'text-gray-l-800'"
            >
                {{ formattedEnergy }}
            </div>
        </div>
        <div class="px-4 flex flex-col justify-center items-center gap-2">
            <div
                class="text-[11px] 425:text-[13px] font-normal leading-[13px] 425:leading-4"
                :class="isDisabled ? 'text-gray-disabled' : 'text-gray-l-500'"
            >
                {{ $t('simulation.charge.results.overview.labels.duration') }}
            </div>
            <div
                class="text-[15px] 425:text-[17px] font-bold leading-[18px] 425:leading-5"
                :class="isDisabled ? 'text-gray-disabled' : 'text-gray-l-800'"
            >
                {{ chargeDuration }}
            </div>
        </div>
        <div class="px-4 flex flex-col justify-center items-center gap-2">
            <div
                class="text-[11px] 425:text-[13px] font-normal leading-[13px] 425:leading-4"
                :class="isDisabled ? 'text-gray-disabled' : 'text-gray-l-500'"
            >
                {{ $t('simulation.charge.results.overview.labels.amount') }}
            </div>
            <div :class="amountValueStyles">
                {{ formattedAmount }}
            </div>
        </div>
    </div>
</template>
